import {
  Alert,
  Button,
  Grid,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import Hidden from '@/components/ui/TzoHidden';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const Form990DueDates = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title=" IRS 990 Forms Series Filing Due Dates"
          description="Learn the important due dates for Form 990 and extensions. Stay on top of deadlines to avoid penalties with easy filing through TaxZerone."
          keywords=""
          canonical="resources/tax-exempt/form-990-due-dates/"
          ogType="article"
          jsonLdPricing="89.99"
          jsonLdAggregateRatingValue="4.99"
          jsonLdAggregateRatingCount="2063"
        />
      }
    >
      <Container maxWidth="lg" sx={{ textAlign: 'center' }}>
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mb: 3,
            mt: 5,
            textAlign: 'center',
          }}
        >
          990 Forms{' '}
          <span className="highlight-container">
            <span className="highlight">Series Due Dates </span>
          </span>
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
            fontWeight: 400,
            mt: 1,
            mb: 2,
          }}
        >
          E-file Forms 990-N, 990, 990-EZ, 990-PF or 990-T before the deadline
          <br /> to maintain your exempt status.
        </Typography>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Button
            variant="contained"
            className="btn-primary medium"
            sx={{
              fontSize: {
                xs: '16px !important',
                sm: '18px !important',
                lg: '24px !important',
              },
              transition: 'all 0.3s ease 0s',
              '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
            }}
            href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=990duedates`}
          >
            E-file and Stay Exempt
          </Button>
        </Box>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Box
                  itemScope
                  itemType="https://schema.org/Article"
                  sx={{
                    p: 3,
                    px: 3,
                    backgroundColor: '#f0f5f8',
                    borderLeft: '5px solid #01456c',
                    mb: 4,
                    borderRadius: 3,
                    position: 'relative',
                  }}
                >
                  <link
                    itemProp="mainEntityOfPage"
                    href="https://www.taxzerone.com/resources/tax-exempt/form-990-due-dates/"
                  />

                  {/* Article requires an image */}
                  <div
                    itemProp="image"
                    itemScope
                    itemType="https://schema.org/ImageObject"
                    style={{ display: 'none' }}
                  >
                    <link
                      itemProp="url"
                      href="https://www.taxzerone.com/assets/images/logo.webp"
                    />
                    <data itemProp="width" value="380" />
                    <data itemProp="height" value="124" />
                  </div>

                  <Box
                    sx={{
                      display: 'grid',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      textAlign: 'left',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      Last updated:{' '}
                      <time
                        itemProp="datePublished"
                        dateTime="2026-07-20T09:00:00-05:00"
                      >
                        July 20, 2026
                      </time>
                      <time
                        itemProp="dateModified"
                        dateTime="2026-07-20T09:00:00-05:00"
                        style={{ display: 'none' }}
                      >
                        July 20, 2026
                      </time>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                      itemProp="author"
                      itemScope
                      itemType="https://schema.org/Person"
                    >
                      -by{' '}
                      <span itemProp="name">
                        <a
                          itemProp="url"
                          href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}`}
                          style={{ color: 'inherit', textDecoration: 'none' }}
                        >
                          Peter Samuel
                        </a>
                      </span>{' '}
                      | TaxZerone
                    </Typography>
                  </Box>
                  <Typography
                    variant="h4"
                    itemProp="headline"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      fontSize: { md: 20, sm: 18, xs: 16 },
                      color: '#01456c',
                      fontStyle: 'italic',
                    }}
                  >
                    Form 990 Due Dates at a Glance
                  </Typography>
                  <Box
                    component="ul"
                    itemProp="articleBody"
                    sx={{
                      mb: 0,
                      pl: 0,
                      listStyle: 'none',

                      '& li': {
                        mb: 1.6,
                        position: 'relative',
                        pl: 3,
                      },

                      '& li::before': {
                        content: '"★"',
                        position: 'absolute',
                        left: 0,
                        top: '-2px',
                        color: '#01456c',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <li>
                      <span>
                        <strong style={{ fontWeight: 600 }}>
                          Forms Covered:
                        </strong>{' '}
                        Filing deadlines for{' '}
                        <strong style={{ fontWeight: 600 }}>
                          Form 990, Form 990-EZ, Form 990-PF, Form 990-T{' '}
                        </strong>{' '}
                        and
                        <strong style={{ fontWeight: 600 }}>
                          Form 990-N (e-Postcard).
                        </strong>
                      </span>
                    </li>

                    <li>
                      <span>
                        <strong style={{ fontWeight: 600 }}>
                          Standard Due Date:
                        </strong>{' '}
                        Most{' '}
                        <Link
                          href="https://www.taxzerone.com/tax-exempt/"
                          sx={{
                            color: '#0202ff',
                            textDecoration: 'underline',
                          }}
                        >
                          Form 990 series
                        </Link>{' '}
                        returns are due by the{' '}
                        <strong style={{ fontWeight: 600 }}>
                          15th day of the 5th month
                        </strong>{' '}
                        after the end of the organization's tax year (May 15 for
                        most calendar-year filers).
                      </span>
                    </li>

                    <li>
                      <span>
                        <strong style={{ fontWeight: 600 }}>
                          Form 990-T Exception: Employee trusts
                        </strong>{' '}
                        generally{' '}
                        <Link
                          href="https://www.taxzerone.com/tax-exempt/e-file-irs-form-990-t/"
                          sx={{
                            color: '#0202ff',
                            textDecoration: 'underline',
                          }}
                        >
                          file Form 990-T
                        </Link>{' '}
                        by the{' '}
                        <strong style={{ fontWeight: 600 }}>
                          15th day of the 4th month
                        </strong>
                        , while most other exempt organizations follow the
                        standard due date.
                      </span>
                    </li>

                    <li>
                      <span>
                        <Link
                          href="https://www.taxzerone.com/tax-extension/efile-irs-form-8868-exempt-organization-tax-extension-online/"
                          sx={{
                            color: '#0202ff',
                            textDecoration: 'underline',
                          }}
                        >
                          Extension:
                        </Link>{' '}
                        Eligible organizations may request an automatic{' '}
                        <strong style={{ fontWeight: 600 }}>
                          6-month extension
                        </strong>{' '}
                        by filing IRS{' '}
                        <strong style={{ fontWeight: 600 }}>Form 8868</strong>{' '}
                        on or before the original due date.{' '}
                        <Link
                          href="https://epostcard.taxzerone.com/?ref=menu&_gl=1*jgipae*_gcl_au*ODg5ODU2MDY2LjE3ODQwOTMzNTg."
                          sx={{
                            color: '#0202ff',
                            textDecoration: 'underline',
                          }}
                        >
                          IRS Form 990-N
                        </Link>{' '}
                        <strong style={{ fontWeight: 600 }}>
                          (e-Postcard)
                        </strong>{' '}
                        is{' '}
                        <strong style={{ fontWeight: 600 }}>
                          not eligible{' '}
                        </strong>
                        for an extension.
                      </span>
                    </li>

                    <li>
                      <span>
                        <strong style={{ fontWeight: 600 }}>
                          Tax Year Rules:
                        </strong>{' '}
                        Filing deadlines are based on your organization's{' '}
                        <strong style={{ fontWeight: 600 }}>
                          tax year-end
                        </strong>
                        , so calendar-year and fiscal-year organizations have
                        different due dates.
                      </span>
                    </li>
                  </Box>

                  {/* Publisher needs an Organization with a logo ImageObject — no meta tags, same pattern */}
                  <div
                    itemProp="publisher"
                    itemScope
                    itemType="https://schema.org/Organization"
                    style={{ display: 'none' }}
                  >
                    <span itemProp="name">TaxZerone</span>
                    <div
                      itemProp="logo"
                      itemScope
                      itemType="https://schema.org/ImageObject"
                    >
                      <link
                        itemProp="url"
                        href="https://www.taxzerone.com/assets/images/logo.webp"
                      />
                      <data itemProp="width" value="380" />
                      <data itemProp="height" value="124" />
                    </div>
                  </div>
                </Box>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  Form 990 is used by{' '}
                  <Link href="/tax-exempt/" sx={{ color: '#0202ff' }}>
                    tax-exempt organizations
                  </Link>{' '}
                  to provide the IRS with financial information. It helps ensure
                  transparency, reports income, expenses, and activities, and
                  allows the public and government to evaluate the
                  organization’s operations and compliance with tax laws.
                </Typography>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  When is Form 990 due?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Form 990 is due by the{' '}
                  <span style={{ fontWeight: 600 }}>
                    15th day of the 5th month
                  </span>{' '}
                  after the end of your organization&apos;s accounting period.
                  <span style={{ fontStyle: 'italic' }}> For example:</span>
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }} component="ul">
                  <li style={{ marginBottom: 8 }}>
                    If your fiscal year ends December 31, your Form 990 is due
                    by <span style={{ fontWeight: 600 }}> May 15</span>.{' '}
                  </li>
                  <li>
                    If your fiscal year ends June 30, your Form 990 is due by{' '}
                    <span style={{ fontWeight: 600 }}>November 15</span>.{' '}
                  </li>
                </Typography>
                <Box
                  component="div"
                  sx={{ mb: 4, p: 2, borderRadius: 2 }}
                  className="gradient-bg"
                >
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box sx={{ width: '100%' }}>
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: 600, mb: 1, fontSize: 18 }}
                      >
                        Don’t miss the May 15 deadline—e-file your exempt
                        organization return now!
                      </Typography>
                      <Typography variant="body2">
                        E-file your 990 series return on time with
                        TaxZerone—simple and efficient.
                      </Typography>
                    </Box>
                    <Box
                      sx={{ width: '100%', maxWidth: 120, textAlign: 'center' }}
                    >
                      <Button
                        variant="contained"
                        sx={{
                          textTransform: 'none',
                          boxShadow: 'none',
                          borderRadius: 2,
                          backgroundColor: '#fff',
                          color: '#333',
                          '&:hover, &:focus, &:active': {
                            color: '#fff',
                            background: '#172b6b',
                          },
                        }}
                        size="large"
                        href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=990duedates`}
                        id="choose"
                      >
                        E-file Now
                      </Button>
                    </Box>
                  </Stack>
                </Box>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Form 990 Series Filing Deadlines - Tax Year 2025
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Use the table below to determine the due date for annual
                  returns (Forms 990-N, 990, 990-EZ, 990-PF) that a tax-exempt
                  organization needs to file. To use the table, you will need to
                  know the end date of your organization’s tax year.
                </Typography>
                <Box component="div" sx={{ mb: 3 }}>
                  <TableContainer
                    sx={{ border: '1px solid #828282', borderRadius: 2 }}
                  >
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#ccc' }}>
                          <TableCell
                            sx={{
                              color: '#444',
                              width: 200,
                              borderRight: '1px solid #828282',
                              textAlign: 'center',
                            }}
                          >
                            Ending date of tax year
                          </TableCell>

                          <TableCell
                            sx={{
                              color: '#444',
                              width: 200,
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Due Date for Form 990-N, 990, 990-EZ, 990-PF
                          </TableCell>
                          <TableCell
                            sx={{
                              color: '#444',
                              width: 200,
                              textAlign: 'center',
                            }}
                          >
                            Extended due date
                            <br />
                            <span style={{ fontWeight: 400, fontSize: 13 }}>
                              {' '}
                              (If you’ve requested an extension with Form 8868)
                            </span>
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={{}}>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            December 31, 2025
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            May 15, 2026
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center' }}>
                            November 16, 2026
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            January 30, 2026
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            June 15, 2026
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center' }}>
                            December 15, 2026
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            February 28, 2026
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            July 15, 2026
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                            }}
                          >
                            January 15, 2027
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            March 31, 2026
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            August 17, 2026
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center' }}>
                            February 16, 2027 #
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            April 30, 2026
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            September 15, 2026
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                            }}
                          >
                            March 15, 2027
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            May 31, 2026
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            October 15, 2026
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center' }}>
                            April 15, 2027
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            June 30, 2026
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            November 16, 2026
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                            }}
                          >
                            May 17, 2027 #
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            July 31, 2026
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            December 15, 2026
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center' }}>
                            June 15, 2027
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            August 31, 2026
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            January 15, 2027
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                            }}
                          >
                            July 15, 2027
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            September 30, 2026
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            February 16, 2027
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center' }}>
                            August 16, 2027
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            October 31, 2026
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            March 15, 2027
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                            }}
                          >
                            September 15, 2027
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            November 30, 2026
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            April 15, 2027
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                            }}
                          >
                            October 15, 2027
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>

                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 4, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <strong style={{ fontWeight: 600 }}>Note:</strong> Dates
                  marked with # indicate a due date that falls on a weekend or
                  holiday, extended to the next business day.
                </Alert>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  When is Form 990-T Due?
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  The due date for{' '}
                  <Link
                    href="/tax-exempt/e-file-irs-form-990-t/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    IRS Form 990-T
                  </Link>{' '}
                  varies based on the type of organization filing the return.
                </Typography>
                <Box component="div" sx={{ mb: 3 }}>
                  <TableContainer
                    sx={{ border: '1px solid #828282', borderRadius: 2 }}
                  >
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#ccc' }}>
                          <TableCell
                            sx={{
                              color: '#444',
                              width: 200,
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Organization Type
                          </TableCell>

                          <TableCell
                            sx={{
                              color: '#444',
                              width: 200,
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Due Date
                          </TableCell>
                          <TableCell
                            sx={{
                              color: '#444',
                              width: 200,
                              textAlign: 'center',
                            }}
                          >
                            Calendar Tax Year
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={{}}>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Employees&apos; trust (401(a)), IRA (Traditional,
                            SEP, SIMPLE), Roth IRA, Coverdell ESA, Archer MSA
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            <span style={{ fontWeight: 600 }}>
                              {' '}
                              15th day of the 4th month
                            </span>{' '}
                            after tax year-end
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center' }}>
                            April 15
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            All other organizations
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            <span style={{ fontWeight: 600 }}>
                              {' '}
                              15th day of the 5th month
                            </span>{' '}
                            after tax year-end
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center' }}>
                            May 15
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  <span style={{ fontWeight: 600 }}> Extensions.</span> Filers
                  may request an automatic extension of time to file{' '}
                  <span style={{ fontWeight: 600 }}>Form 990-T</span> by using{' '}
                  <br />
                  <Link
                    href="/tax-extension/efile-irs-form-8868-exempt-organization-tax-extension-online/"
                    sx={{ color: '#0202ff' }}
                  >
                    Form 8868
                  </Link>
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Which Form 990 should you file
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  The type of Form 990 your organization must file depends on
                  your gross receipts and total assets:{' '}
                </Typography>
                <Box component="div" sx={{ mb: 3 }}>
                  <TableContainer
                    sx={{ border: '1px solid #828282', borderRadius: 2 }}
                  >
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#ccc' }}>
                          <TableCell
                            sx={{
                              color: '#444',
                              width: 200,
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Form Type
                          </TableCell>

                          <TableCell
                            sx={{
                              color: '#444',
                              width: 300,
                              textAlign: 'center',
                            }}
                          >
                            Filing Requirements
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={{}}>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            <Link
                              href={`${process.env.NEXT_PUBLIC_990N_SITE_URL}?ref=990duedates`}
                              sx={{ color: '#0202ff' }}
                            >
                              {' '}
                              Form 990-N (e-Postcard)
                            </Link>
                          </TableCell>

                          <TableCell sx={{ textAlign: 'left' }}>
                            For organizations with gross receipts ≤ $50,000
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            <Link
                              href="/tax-exempt/e-file-irs-form-990-ez/"
                              sx={{ color: '#0202ff' }}
                            >
                              Form 990-EZ{' '}
                            </Link>
                          </TableCell>

                          <TableCell sx={{ textAlign: 'left' }}>
                            For organizations with gross receipts &lt; $200,000
                            and total assets &lt; $500,000
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            <Link
                              href="/tax-exempt/e-file-irs-form-990/"
                              sx={{ color: '#0202ff' }}
                            >
                              {' '}
                              Form 990{' '}
                            </Link>
                          </TableCell>

                          <TableCell sx={{ textAlign: 'left' }}>
                            For organizations with gross receipts  ≥ $200,000 or
                            total assets  ≥ $500,000
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            <Link
                              href="/tax-exempt/e-file-irs-form-990-pf/"
                              sx={{ color: '#0202ff' }}
                            >
                              {' '}
                              Form 990-PF
                            </Link>
                          </TableCell>

                          <TableCell sx={{ textAlign: 'left' }}>
                            Required for all private foundations, regardless of
                            financial status
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            <Link
                              href="/tax-exempt/e-file-irs-form-990-t/"
                              sx={{ color: '#0202ff' }}
                            >
                              {' '}
                              Form 990-T{' '}
                            </Link>
                          </TableCell>

                          <TableCell sx={{ textAlign: 'left' }}>
                            Required for exempt organizations with $1,000+ gross
                            income from unrelated business activities
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Can I extend my 990 filing due date?
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> Yes</span>, you can extend
                  your 990 Forms filing due date. Most tax-exempt organizations
                  are eligible for an automatic{' '}
                  <span style={{ fontWeight: 600 }}> 6-month extension</span> by
                  filing{' '}
                  <Link
                    href="/tax-extension/efile-irs-form-8868-exempt-organization-tax-extension-online/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    IRS Form 8868
                  </Link>{' '}
                  on or before their original due date.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  This extension applies to all forms in the 990 series{' '}
                  <span style={{ fontWeight: 600 }}>except Form 990-N</span>,
                  which is not eligible for an extension.
                </Typography>

                <Box
                  sx={{
                    textAlign: 'center',
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: '#f9f9f9',
                    mb: 4,
                    border: '1px solid #ccc',
                  }}
                >
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    If your organization’s fiscal year ends on{' '}
                    <span style={{ fontWeight: 600 }}>December 31</span>, your
                    990 series forms are due by{' '}
                    <span style={{ fontWeight: 600 }}>May 15</span>. To get a
                    6-month extension, you must file Form 8868{' '}
                    <span style={{ fontWeight: 600 }}>on or before May 15</span>
                    .
                  </Typography>

                  <Button
                    variant="contained"
                    color="primary"
                    sx={{
                      textTransform: 'none',
                      boxShadow: 'none',
                      borderRadius: 2,
                    }}
                    size="large"
                    href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=990duedates`}
                  >
                    File 8868 Now
                  </Button>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Why Choose TaxZerone to File Your 990 Series Return?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Filing your 990 Series return doesn’t have to be complicated.
                  As an IRS-authorized provider, TaxZerone supports all 990
                  forms—{' '}
                  <Link
                    href={`${process.env.NEXT_PUBLIC_990N_SITE_URL}?ref=990duedates`}
                    sx={{ color: '#0202ff' }}
                  >
                    990-N
                  </Link>
                  ,{' '}
                  <Link
                    href="/tax-exempt/e-file-irs-form-990/"
                    sx={{ color: '#0202ff' }}
                  >
                    990
                  </Link>
                  ,{' '}
                  <Link
                    href="/tax-exempt/e-file-irs-form-990-ez/"
                    sx={{ color: '#0202ff' }}
                  >
                    990-EZ
                  </Link>
                  ,{' '}
                  <Link
                    href="/tax-exempt/e-file-irs-form-990-pf/"
                    sx={{ color: '#0202ff' }}
                  >
                    990-PF
                  </Link>
                  , and{' '}
                  <Link
                    href="/tax-exempt/e-file-irs-form-990-t/"
                    sx={{ color: '#0202ff' }}
                  >
                    990-T
                  </Link>
                  —in one place. Required 990 schedules are auto generated at{' '}
                  <span style={{ fontWeight: 600 }}>no extra cost</span>.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  With{' '}
                  <Link href="/features/click2file/" sx={{ color: '#0202ff' }}>
                    Click2File
                  </Link>
                  , you can carry over prior-year data quickly and accurately.
                  Your filed returns are securely stored and always accessible.
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>
      <Container maxWidth="lg" sx={{ mb: 5, my: { xs: 6, sm: 6, md: 6 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 2, md: 5 }}
        >
          <Box
            sx={{
              width: '100%',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 3,
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              Looking to file 990 series forms? Get started with ease.
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                fontWeight: 400,
                color: '#333333',
                mb: 2,
              }}
            >
              TaxZerone allows you to{' '}
              <span style={{ fontWeight: 600 }}> e-file your 990 series</span>{' '}
              forms quickly and securely. Enjoy a smooth process{' '}
              <Hidden smDown>
                <br />
              </Hidden>
              with minimal effort, ensuring timely compliance with tax
              deadlines.
            </Typography>

            <Box sx={{ textAlign: { xs: 'center', md: 'center' } }}>
              <Button
                variant="contained"
                sx={{
                  mt: 1,
                  transition: 'all 0.3s ease 0s',
                  '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                }}
                className="btn-primary"
                href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=990duedates`}
              >
                Start Filing Now!
              </Button>
            </Box>
          </Box>
        </Stack>
      </Container>
    </AppLayout>
  );
};

export default Form990DueDates;
